import {
  parseThemeChoice,
  resolveTheme,
  type ResolvedTheme,
  type ThemeChoice,
} from "./resolve-theme";

/**
 * Reactive light/dark/system theme store — the theme sibling of `lib/i18n.ts`'s
 * locale store. The chosen mode is applied by toggling `.dark` on
 * `<html>` (matching the branding `html.dark{}` block + Tailwind's
 * `.dark` variant) and mirrored into two cookies so the root layout can stamp
 * the correct class server-side **before paint** (no flash, no inline script —
 * CSP-safe, spec §4.4):
 *   - `ui-theme`         = the RESOLVED mode (light|dark) the server stamps.
 *   - `ui-theme-choice`  = the raw choice (light|dark|system) for reconciliation.
 *
 * Server persistence (follows the user across devices) is handled separately by
 * the Appearance card / ThemeSync via the `ui.theme` user preference; this store
 * only owns the instant local application + the no-flash cookies.
 */

const CHOICE_STORAGE_KEY = "ui-theme-choice";
const RESOLVED_COOKIE = "ui-theme";
const CHOICE_COOKIE = "ui-theme-choice";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

let currentChoice: ThemeChoice = "system";
const listeners = new Set<() => void>();

function systemPrefersDark(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

function writeCookie(name: string, value: string): void {
  if (typeof document === "undefined") return;
  // Not `__Host-`/Secure: it must be readable server-side over the dev HTTP
  // origin and is non-sensitive (a render preference). SameSite=Lax is enough.
  document.cookie = `${name}=${value}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
}

function apply(choice: ThemeChoice): void {
  if (typeof document === "undefined") return;
  const resolved: ResolvedTheme = resolveTheme(choice, systemPrefersDark());
  document.documentElement.classList.toggle("dark", resolved === "dark");
  writeCookie(RESOLVED_COOKIE, resolved);
  writeCookie(CHOICE_COOKIE, choice);
}

/** Reads the persisted local choice on first load and applies it. */
function init(): void {
  if (typeof window === "undefined") return;
  const stored = parseThemeChoice(localStorage.getItem(CHOICE_STORAGE_KEY));
  if (stored) {
    currentChoice = stored;
  }
  apply(currentChoice);

  // Keep a `system` choice live as the OS preference flips.
  if (typeof window.matchMedia === "function") {
    window
      .matchMedia("(prefers-color-scheme: dark)")
      .addEventListener("change", () => {
        if (currentChoice === "system") {
          apply(currentChoice);
          listeners.forEach((l) => l());
        }
      });
  }
}

export function getThemeChoice(): ThemeChoice {
  return currentChoice;
}

export function getResolvedTheme(): ResolvedTheme {
  return resolveTheme(currentChoice, systemPrefersDark());
}

/**
 * Sets the local theme choice: persists to localStorage, applies `.dark`
 * instantly, re-stamps the cookies, and notifies subscribers. Server
 * persistence (the `ui.theme` preference) is the caller's concern.
 */
export function setThemeChoice(choice: ThemeChoice): void {
  currentChoice = choice;
  if (typeof window !== "undefined") {
    localStorage.setItem(CHOICE_STORAGE_KEY, choice);
  }
  apply(choice);
  listeners.forEach((l) => l());
}

export function subscribeTheme(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// Initialize on module load (client only).
if (typeof window !== "undefined") {
  init();
}
